import React from "react";
import Link from "next/link";
import HamburgerMenu from "./HamburgerMenu";
import DesktopMenu from "./DesktopMenu";

export interface Route {
  name: string;
  link: string;
  /** Extra path prefixes that should light this heading up — for children that
   *  don't live under `link` (see app/lib/navActive.ts). */
  matchPaths?: string[];
  /** Path prefixes that live under `link` but belong to another heading — the
   *  clinic pages sit at /about/… but hang off Treatments. */
  excludePaths?: string[];
  secondaryLinks?: { name: string; link: string }[];
}

export const routes: Route[] = [
  {
    name: "Work Together",
    link: "/workwithme",
    secondaryLinks: [
      { name: "Farleigh Method", link: "/workwithme/farleighMethod" },
      { name: "One-to-One Consultation", link: "/workwithme/consultation" },
      { name: "Follow Up", link: "/workwithme/flexibleConsult" },
      { name: "The Bodhi Club", link: "/workwithme/align" },
    ],
  },
  {
    name: "Treatments",
    link: "/treatments",
    matchPaths: [
      "/dayclinic",
      "/about/wellbeing-southport",
      "/about/wellbeing-manchester",
    ],
    secondaryLinks: [
      { name: "Day Clinic", link: "/dayclinic" },
      { name: "Body Ballancer", link: "/treatments/body-ballancer" },
      { name: "Massage & Bodywork", link: "/treatments" },
      { name: "Retreats", link: "/retreats" },
      { name: "Southport Clinic", link: "/about/wellbeing-southport" },
      { name: "Manchester Clinic", link: "/about/wellbeing-manchester" },
    ],
  },
  {
    name: "Shop",
    link: "/shop",
    secondaryLinks: [
      { name: "Shop All", link: "/shop" },
      { name: "6 Day Guided Cleanse", link: "/shop/feature" },
    ],
  },
  {
    name: "Learn",
    link: "/blog",
    secondaryLinks: [
      { name: "Blog", link: "/blog" },
      { name: "Nutrition", link: "/nutrition" },
      { name: "Instagram", link: "/blog/instagram" },
    ],
  },
];

/** About lives in the footer + the Niki avatar on desktop; on mobile the avatar
 *  is hidden (no room next to the hamburger + CTA) so it rides in the drawer. */
export const mobileAboutRoute: Route = {
  name: "About",
  link: "/about",
  secondaryLinks: [
    { name: "Our Story", link: "/about" },
    { name: "About Niki", link: "/about/founder" },
  ],
};

// Token reference — nav sits on --bg-page (burgundy dark):
//   nav text:       --text-on-dark       (cream)
//   nav text muted: --text-on-dark-muted (sand)
//   nav border:     --border-on-dark
//   CTA bg:         --btn-inverse-bg     (cream)
//   CTA text:       --btn-inverse-text   (burgundy ink)
//   CTA hover bg:   --btn-inverse-hover  (cream-dk)
//   focus ring:     --focus-ring         (sage)

const NavBar: React.FC = () => {
  return (
    <nav
      aria-label="Main navigation"
      className="border-b border-[var(--border-on-dark)]"
    >
      {/* ── Mobile layout: flex — hamburger keeps its width, CTA shrinks ── */}
      <div className="lg:hidden flex items-center gap-2 px-2 py-3">
        {/* Hamburger left — never shrinks, always on top (see z-index in HamburgerMenu) */}
        <div className="flex justify-start shrink-0">
          <HamburgerMenu routes={[...routes, mobileAboutRoute]} />
        </div>

        {/* CTA right — compact pill on phones ("Free call"), full wording from
            sm up. The drawer carries the long "Book a free discovery call". */}
        <div className="flex justify-end min-w-0">
          <Link
            href="/workwithme/discoverycall"
            aria-label="Book a free discovery call"
            className="
              px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl
              text-[13px] sm:text-sm font-medium leading-none whitespace-nowrap
              bg-[var(--btn-inverse-bg)] text-[var(--btn-inverse-text)]
              hover:bg-[var(--btn-inverse-hover)] transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]
            "
          >
            <span className="sm:hidden">Free call</span>
            <span className="hidden sm:inline">Free discovery call</span>
          </Link>
        </div>
      </div>

      {/* ── Desktop layout ── */}
      <div className="hidden lg:flex items-center px-4 py-3 container mx-auto">
        <DesktopMenu routes={routes} />
      </div>
    </nav>
  );
};

export default NavBar;
